import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, CheckCircle, Cloud, Shield, Sparkles, Rocket } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import GetStartedDialog from "@/components/GetStartedDialog";
import riseImage from "@/assets/cloud-computing-network.jpg";

const RiseWithSAPDetail = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const offerings = [
    {
      icon: Cloud,
      title: "SAP S/4HANA Cloud",
      description: "Access the intelligent ERP suite with built-in best practices and continuous innovation"
    },
    {
      icon: Shield,
      title: "Business Process Intelligence",
      description: "Insights and recommendations to optimize your business processes using AI and analytics"
    },
    {
      icon: Sparkles,
      title: "Business Technology Platform",
      description: "Extend and integrate your landscape with low-code/no-code tools and integration services"
    },
    {
      icon: Rocket,
      title: "Managed Services",
      description: "Application management, security, compliance, and continuous improvement services"
    }
  ];

  const benefits = [
    {
      category: "Speed to Value",
      items: [
        "Fit-to-standard approach using SAP best practices",
        "Activate methodology reduces implementation to 3-6 months",
        "Pre-configured business processes for 25+ industries",
        "SAP Model Company provides reference configurations"
      ]
    },
    {
      category: "Total Cost of Ownership",
      items: [
        "Subscription includes hosting, database, and middleware costs",
        "SAP manages infrastructure on hyperscaler (AWS, Azure, or GCP)",
        "Quarterly innovation updates included at no additional cost",
        "Eliminates hardware refresh and datacenter expenses"
      ]
    },
    {
      category: "Innovation & Agility",
      items: [
        "Quarterly innovation updates automatically delivered",
        "Access to latest SAP technologies and features",
        "Flexible scaling as your business grows",
        "Integration with emerging technologies (AI, ML, IoT)"
      ]
    },
    {
      category: "Business Transformation",
      items: [
        "End-to-end business process transformation",
        "Industry-specific solutions and templates",
        "Clean core approach for sustainable innovation",
        "Expert guidance throughout your journey"
      ]
    }
  ];

  const services = [
    {
      title: "Discovery & Assessment",
      description: "Understand your current landscape and define transformation roadmap with SAP experts"
    },
    {
      title: "Implementation Services",
      description: "Deploy SAP S/4HANA Cloud with best practices using proven methodologies and tools"
    },
    {
      title: "Integration & Extension",
      description: "Connect existing systems and extend capabilities using SAP Business Technology Platform"
    },
    {
      title: "Managed Operations",
      description: "Ongoing application management, monitoring, support, and continuous improvement"
    },
    {
      title: "Business Process Services",
      description: "Optimize and transform business processes with intelligence-driven insights"
    },
    {
      title: "Learning & Support",
      description: "User enablement, training, and 24/7 support to maximize user adoption and value"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <div className="pt-20 pb-16">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-r from-blue-600/10 via-blue-500/5 to-background py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Button 
              variant="ghost" 
              onClick={() => window.history.back()}
              className="mb-6"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            
            <div className="space-y-4">
              <Badge className="bg-blue-600 text-white hover:bg-blue-700">
                Business Transformation as a Service
              </Badge>
              <h1 className="text-4xl md:text-5xl font-bold text-foreground">
                RISE with SAP
              </h1>
              <p className="text-xl text-muted-foreground max-w-3xl">
                SAP's bundled offering combining S/4HANA Cloud, BTP platform credits, Business Network integration, and managed cloud services. Subscription-based pricing with infrastructure, tools, and implementation support included.
              </p>
            </div>
          </div>
        </div>

        {/* Hero Image */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="rounded-xl overflow-hidden shadow-2xl">
            <img 
              src={riseImage} 
              alt="RISE with SAP cloud transformation showing connected business network"
              className="w-full h-auto"
            />
          </div>
        </div>

        {/* What's Included */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Package Components</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Subscription includes S/4HANA Cloud access, BTP credits, tools, and SAP-managed infrastructure
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {offerings.map((offering, idx) => (
              <Card key={idx} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <offering.icon className="h-12 w-12 text-blue-600 mb-4" />
                  <CardTitle className="text-lg">{offering.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">{offering.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Business Benefits */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Key Benefits</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Transform your business with measurable outcomes
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {benefits.map((benefit, idx) => (
              <Card key={idx}>
                <CardHeader>
                  <CardTitle className="text-xl">{benefit.category}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {benefit.items.map((item, iIdx) => (
                      <li key={iIdx} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Services & Support */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Services & Support</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              End-to-end services to ensure success at every stage
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, idx) => (
              <Card key={idx} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg">{service.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Why Choose RISE */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Card className="bg-gradient-to-br from-blue-600/10 via-background to-blue-500/5">
            <CardHeader>
              <CardTitle className="text-2xl">Why Choose RISE with SAP?</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-lg mb-4 text-blue-600">Traditional Approach</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• Complex multi-vendor procurement</li>
                    <li>• High upfront capital investment</li>
                    <li>• 12-18 month implementation timelines</li>
                    <li>• Manual upgrade and maintenance burden</li>
                    <li>• Limited access to latest innovations</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-4 text-green-600">RISE with SAP</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• Single integrated solution package</li>
                    <li>• Subscription-based pricing model</li>
                    <li>• 4-6 month rapid deployment</li>
                    <li>• Automatic updates and innovation</li>
                    <li>• Continuous access to new capabilities</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* CTA */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Card className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
            <CardContent className="p-8 text-center">
              <h2 className="text-3xl font-bold mb-4">
                Ready to RISE?
              </h2>
              <p className="text-xl mb-6 text-white/90">
                Start your transformation journey with RISE with SAP today
              </p>
              <GetStartedDialog />
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RiseWithSAPDetail;
